import { useCallback, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, Check, UploadCloud, X } from 'lucide-react';
import type { MediaAsset } from '../../../shared/types';
import { formatBytes } from '../../../shared/utils';
import { Progress } from '../ui';
import { api, ApiError } from '../../lib/api';
import { cn } from '../../lib/utils';

interface QueueItem {
  id: string;
  name: string;
  size: number;
  status: 'uploading' | 'done' | 'error';
  error?: string;
}

export interface UploadDropzoneProps {
  onUploaded: (asset: MediaAsset) => void;
  className?: string;
}

const ACCEPT = 'image/*,video/*';

/**
 * Drag-and-drop + click-to-browse multi-file uploader. The API client sends
 * uploads over `fetch`, which does not expose byte-level progress, so each
 * queued file shows an indeterminate bar while in flight and settles into a
 * done/error state — real progress per file, just not per byte.
 */
export function UploadDropzone({ onUploaded, className }: UploadDropzoneProps) {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);

  const upload = useCallback(
    (file: File) => {
      const id = crypto.randomUUID();
      setItems((current) => [
        ...current,
        { id, name: file.name, size: file.size, status: 'uploading' },
      ]);

      api.media
        .upload(file)
        .then((response) => {
          setItems((current) =>
            current.map((item) => (item.id === id ? { ...item, status: 'done' } : item)),
          );
          onUploaded(response.asset);
          window.setTimeout(() => {
            setItems((current) => current.filter((item) => item.id !== id));
          }, 2200);
        })
        .catch((error: unknown) => {
          setItems((current) =>
            current.map((item) =>
              item.id === id
                ? {
                    ...item,
                    status: 'error',
                    error: error instanceof ApiError ? error.message : 'Upload failed.',
                  }
                : item,
            ),
          );
        });
    },
    [onUploaded],
  );

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList) return;
      Array.from(fileList).forEach(upload);
    },
    [upload],
  );

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    handleFiles(event.dataTransfer.files);
  };

  const dismiss = (id: string) => setItems((current) => current.filter((item) => item.id !== id));

  return (
    <div className={className}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') inputRef.current?.click();
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragEnter={(event) => {
          event.preventDefault();
          dragDepth.current += 1;
          setDragging(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          dragDepth.current -= 1;
          if (dragDepth.current <= 0) setDragging(false);
        }}
        onDrop={onDrop}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors',
          dragging
            ? 'border-accent bg-accent-soft'
            : 'border-border bg-surface/40 hover:border-border-strong hover:bg-elevated/40',
        )}
      >
        <span
          className={cn(
            'flex size-10 items-center justify-center rounded-full bg-elevated text-muted',
            dragging && 'bg-accent-soft text-accent',
          )}
          aria-hidden
        >
          <UploadCloud className="size-5" />
        </span>
        <p className="text-sm font-medium text-fg">
          Drop images or video, or <span className="text-accent">browse</span>
        </p>
        <p className="text-xs text-subtle">Upload as many as you like — they land in the library below.</p>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple
          className="sr-only"
          onChange={(event) => {
            handleFiles(event.target.files);
            event.target.value = '';
          }}
        />
      </div>

      <AnimatePresence>
        {items.length > 0 && (
          <motion.ul
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-3 space-y-2 overflow-hidden"
          >
            {items.map((item) => (
              <li
                key={item.id}
                className="flex items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2"
              >
                <span
                  className={cn(
                    'flex size-7 shrink-0 items-center justify-center rounded-md',
                    item.status === 'error'
                      ? 'bg-danger-soft text-danger'
                      : item.status === 'done'
                        ? 'bg-success-soft text-success'
                        : 'bg-elevated text-muted',
                  )}
                  aria-hidden
                >
                  {item.status === 'error' ? (
                    <AlertCircle className="size-3.5" />
                  ) : item.status === 'done' ? (
                    <Check className="size-3.5" />
                  ) : (
                    <UploadCloud className="size-3.5" />
                  )}
                </span>
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="truncate text-xs font-medium text-fg">{item.name}</p>
                    <span className="shrink-0 text-[11px] text-subtle">{formatBytes(item.size)}</span>
                  </div>
                  {item.status === 'uploading' && <Progress size="sm" value={0} indeterminate />}
                  {item.status === 'error' && (
                    <p className="text-[11px] text-danger">{item.error}</p>
                  )}
                </div>
                <button
                  type="button"
                  aria-label="Dismiss"
                  onClick={() => dismiss(item.id)}
                  className="shrink-0 rounded p-1 text-subtle hover:bg-elevated hover:text-fg"
                >
                  <X className="size-3.5" />
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
